import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { combineLatest, filter, of, switchMap } from 'rxjs';
import { ClipAndSaveComponent } from '../clip-and-save/clip-and-save.component';
import { ClearClipRegion, SetActiveMapTool } from '../map/map.actions';
import { MapTool } from '../map/map.models';
import { MapState } from '../map/map.state';
import { ScandataState } from '../scandata/scandata.state';
import { ClassificationEditComponent } from './classification-edit/classification-edit.component';
import { DownloadStatusComponent } from './download/download-status/download-status.component';
import { ListFiltersComponent } from './list-filters/list-filters.component';
import { SetView } from './options-panel.actions';
import { OptionsPanelView } from './options-panel.models';
import { OptionsPanelState } from './options-panel.state';
import { SelectedDetailsComponent } from './selected-details/selected-details.component';

function isClipMapTool(mapTool: MapTool) {
  return mapTool === MapTool.ClipRectangle || mapTool === MapTool.ClipPolygon;
}

@UntilDestroy()
@Component({
  selector: 'sd-options-panel',
  imports: [
    CommonModule,
    ListFiltersComponent,
    DownloadStatusComponent,
    SelectedDetailsComponent,
    ClassificationEditComponent,
    ClipAndSaveComponent,
  ],
  templateUrl: './options-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionsPanelComponent {
  view = OptionsPanelView;
  optionsPanelView = this.store.selectSignal(OptionsPanelState.view);
  clipRegion = this.store.selectSignal(MapState.clipRegion);

  readonly clipAndSaveGeoJsonFilter = computed(() => {
    const region = this.clipRegion();
    return isDefined(region) ? JSON.stringify(region.polygon) : undefined;
  });

  constructor(private store: Store) {
    this.subscribeToSelectionChanges();
    this.subscribeToViewChanges();
    this.subscribeToClipWorkflow();
  }

  onExitClipAndSaveClick() {
    this.store.dispatch([new ClearClipRegion(), new SetView(OptionsPanelView.None)]);
  }

  onClipSaved() {
    this.store.dispatch([
      new ClearClipRegion(),
      new SetActiveMapTool(MapTool.Default),
      new SetView(OptionsPanelView.None),
    ]);
  }

  private subscribeToSelectionChanges() {
    this.store
      .select(ScandataState.selected)
      .pipe(
        filter((selected) => selected.length > 0),
        switchMap(() =>
          this.store.dispatch([
            new ClearClipRegion(),
            new SetView(OptionsPanelView.SelectedDetails),
          ]),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private subscribeToViewChanges() {
    combineLatest([
      this.store.select(OptionsPanelState.view),
      this.store.select(ScandataState.selected),
      this.store.select(MapState.activeMapTool),
    ])
      .pipe(
        switchMap(([view, { length: selectedCount }, activeMapTool]) => {
          if (
            selectedCount > 0 &&
            view === OptionsPanelView.None &&
            !isClipMapTool(activeMapTool)
          ) {
            return this.store.dispatch(new SetView(OptionsPanelView.SelectedDetails));
          }

          if (selectedCount === 0 && view === OptionsPanelView.SelectedDetails) {
            return this.store.dispatch(new SetView(OptionsPanelView.None));
          }

          return of(void 0);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private subscribeToClipWorkflow() {
    combineLatest([
      this.store.select(MapState.clipRegion),
      this.store.select(MapState.activeMapTool),
      this.store.select(OptionsPanelState.view),
      this.store.select(ScandataState.selected),
    ])
      .pipe(
        switchMap(([clipRegion, activeMapTool, view, selected]) => {
          const selectedCount = selected.length;
          const clipToolActive = isClipMapTool(activeMapTool);

          if (view === OptionsPanelView.ClipAndSave && !clipToolActive) {
            return this.store.dispatch(
              new SetView(
                selectedCount > 0 ? OptionsPanelView.SelectedDetails : OptionsPanelView.None,
              ),
            );
          }

          if (isDefined(clipRegion) && clipToolActive) {
            if (view !== OptionsPanelView.ClipAndSave) {
              return this.store.dispatch(new SetView(OptionsPanelView.ClipAndSave));
            }
            return of(void 0);
          }

          if (!isDefined(clipRegion) && view === OptionsPanelView.ClipAndSave) {
            return this.store.dispatch(
              new SetView(
                selectedCount > 0 ? OptionsPanelView.SelectedDetails : OptionsPanelView.None,
              ),
            );
          }

          return of(void 0);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
